import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { Redirect } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Label } from '@nucleus-commerce/ui/components/label';
import { RadioGroup, RadioGroupItem } from '@nucleus-commerce/ui/components/radio-group';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { useCreateRedirect, useUpdateRedirect } from '../hooks/use-redirect-mutations';
import {
  emptyRedirectForm,
  fromRedirect,
  redirectFormSchema,
  toRedirectCreate,
  toRedirectUpdate,
  type RedirectForm,
} from '../schemas/redirect-form-schema';

const KNOWN_FIELDS: ReadonlySet<string> = new Set(['fromPath', 'toPath', 'statusCode']);

export interface RedirectDialogProps {
  storeId: string;
  /** Null adds a new redirect. */
  redirect: Redirect | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function RedirectFormBody({
  storeId,
  redirect,
  onClose,
}: {
  storeId: string;
  redirect: Redirect | null;
  onClose: () => void;
}) {
  const { t } = useTranslation(['redirects', 'common']);
  const message = useErrorMessage();
  const create = useCreateRedirect(storeId);
  const update = useUpdateRedirect(storeId);
  const pending = create.isPending || update.isPending;
  const form = useForm<RedirectForm>({
    resolver: useI18nResolver(redirectFormSchema),
    defaultValues: redirect ? fromRedirect(redirect) : emptyRedirectForm(),
  });

  const submit = form.handleSubmit(async (values) => {
    try {
      if (redirect) {
        await update.mutateAsync({ id: redirect.id, body: toRedirectUpdate(values) });
      } else {
        await create.mutateAsync(toRedirectCreate(values));
      }
      onClose();
    } catch (error) {
      if (applyFieldErrors(error, form.setError, { knownFields: KNOWN_FIELDS })) return;
      form.setError('root.server', { type: 'server', message: message(error) });
    }
  });

  return (
    <Form {...form}>
      <form onSubmit={submit} noValidate className="flex flex-col gap-4">
        {form.formState.errors.root?.server?.message ? (
          <Alert variant="destructive" role="alert">
            <AlertDescription>{form.formState.errors.root.server.message}</AlertDescription>
          </Alert>
        ) : null}
        <FormField
          control={form.control}
          name="fromPath"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('redirects:fields.fromPath')}</FormLabel>
              <FormControl>
                <Input
                  autoComplete="off"
                  className="font-mono text-[13px]"
                  placeholder="/old-address"
                  readOnly={redirect !== null}
                  autoFocus={redirect === null}
                  {...field}
                />
              </FormControl>
              <FormDescription>
                {redirect
                  ? t('redirects:fields.fromPathLocked')
                  : t('redirects:fields.fromPathHint')}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="toPath"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('redirects:fields.toPath')}</FormLabel>
              <FormControl>
                <Input
                  autoComplete="off"
                  className="font-mono text-[13px]"
                  placeholder="/pages/new-address"
                  autoFocus={redirect !== null}
                  {...field}
                />
              </FormControl>
              <FormDescription>{t('redirects:fields.toPathHint')}</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="statusCode"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('redirects:fields.statusCode')}</FormLabel>
              <FormControl>
                <RadioGroup
                  value={field.value}
                  onValueChange={field.onChange}
                  className="gap-2.5"
                  aria-label={t('redirects:fields.statusCode')}
                >
                  {(['301', '302'] as const).map((code) => (
                    <div key={code} className="flex items-start gap-2.5">
                      <RadioGroupItem
                        value={code}
                        id={`redirect-code-${code}`}
                        className="mt-0.5"
                      />
                      <div className="grid gap-0.5">
                        <Label htmlFor={`redirect-code-${code}`}>
                          {t(`redirects:statusCode.${code}`)}
                        </Label>
                        <span className="text-muted-foreground text-xs">
                          {t(`redirects:statusCodeHint.${code}`)}
                        </span>
                      </div>
                    </div>
                  ))}
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button type="submit" disabled={pending}>
            {pending
              ? t('common:actions.saving')
              : redirect
                ? t('redirects:edit.submit')
                : t('redirects:create.submit')}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}

/** Adds a redirect or changes where an existing one points. */
export function RedirectDialog({ storeId, redirect, open, onOpenChange }: RedirectDialogProps) {
  const { t } = useTranslation('redirects');
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{redirect ? t('edit.title') : t('create.title')}</DialogTitle>
          <DialogDescription>
            {redirect ? t('edit.description') : t('create.description')}
          </DialogDescription>
        </DialogHeader>
        <RedirectFormBody
          storeId={storeId}
          redirect={redirect}
          onClose={() => onOpenChange(false)}
        />
      </DialogContent>
    </Dialog>
  );
}
